/** @jsxRuntime classic */
/** @jsx jsx */
import { useState, useEffect } from "react";
import { jsx, Button, Heading } from "theme-ui";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import "react-tabs/style/react-tabs.css";

import Cellar from "./Cellar";
import Header from "./Header";
import RequestsTable from "./RequestsTable";
import api, { errorMessage } from "../utils/api";
import { retrieveAuth } from "../utils/auth";

export default function Dashboard() {
  const [user, setUser] = useState({});
  const [auth, setAuth] = useState({});
  const [loaded, setLoaded] = useState(false);
  const [deposits, setDeposits] = useState([]);
  const [requests, setRequests] = useState([]);
  const [historicalRequests, setHistoricalRequests] = useState([]);
  const [cellarOwner, setCellarOwner] = useState({});
  const [error, setError] = useState(null);

  useEffect(() => {
    const stored = retrieveAuth();
    if (!stored) return;
    setUser(stored.user);
    setAuth(stored.token);

    Promise.all([
      api.get("/api/deposits/"),
      api.get("/api/requests/"),
      api.get("/api/requests/?selection=historical"),
    ])
      .then(([depositsResp, activeResp, historyResp]) => {
        setDeposits(depositsResp.data.results);
        setCellarOwner(depositsResp.data.metadata.user);
        setRequests(activeResp.data);
        setHistoricalRequests(historyResp.data);
        setLoaded(true);
      })
      .catch((err) => {
        // Without this the spinner ran forever on any failure.
        setError(errorMessage(err, "We couldn't load your cellar."));
        setLoaded(true);
      });
  }, []);

  return (
    <div
      sx={{
        minHeight: "100vh",
      }}
    >
      <Header user={user} />

      <div
        sx={{
          px: [10, 50],
          pb: [10, 50],
        }}
      >
        {error ? (
          <div
            sx={{
              variant: "components.flexCenter",
              flexDirection: "column",
              width: "100%",
              height: 500,
              textAlign: "center",
            }}
          >
            <Heading sx={{ mb: 3 }}>{error}</Heading>
            <Button onClick={() => window.location.reload()}>Try again</Button>
          </div>
        ) : !loaded ? (
          <div
            sx={{
              variant: "components.flexCenter",
              width: "100%",
              height: 500,
            }}
          >
            <span>
              <div className="loader"></div>
            </span>
          </div>
        ) : (
          <div>
            <Tabs>
              <TabList>
                <Tab key="cellar-tab">
                  Cellar {deposits.length > 0 && `(${deposits.length})`}
                </Tab>
                <Tab key="requests-tab">
                  Requests {requests.length > 0 && `(${requests.length})`}
                </Tab>
              </TabList>

              <TabPanel key="cellar">
                {deposits.length ? (
                  <Cellar
                    deposits={deposits}
                    token={auth.token}
                    user={user}
                    cellarOwner={cellarOwner}
                  />
                ) : (
                  <div
                    sx={{
                      variant: "components.flexCenter",
                      width: "100%",
                      height: "calc(100vh - 260px)",
                    }}
                  >
                    <Heading fontSize={[48, 66]}>your cellar is empty</Heading>
                  </div>
                )}
              </TabPanel>
              <TabPanel key="requests">
                <div sx={{ mt: 30 }}>
                  <Heading sx={{ mb: 2 }}>Active Requests</Heading>
                  {requests.length > 0 ? (
                    <RequestsTable data={requests} />
                  ) : (
                    <Placeholder>
                      <div sx={{ opacity: 0.2 }}>no active requests</div>
                    </Placeholder>
                  )}
                </div>

                <div sx={{ mt: 30 }}>
                  <Heading sx={{ mb: 2 }}>History</Heading>
                  {historicalRequests.length > 0 ? (
                    <RequestsTable data={historicalRequests} />
                  ) : (
                    <Placeholder>
                      <div sx={{ opacity: 0.2 }}>no historical requests</div>
                    </Placeholder>
                  )}
                </div>
              </TabPanel>
            </Tabs>
          </div>
        )}
      </div>
    </div>
  );
}

const Placeholder = ({ children }) => {
  return (
    <div
      sx={{
        width: "100%",
        padding: 100,
        border: "1px solid rgba(0, 0, 0, 0.1)",
        borderRadius: 10,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {children}
    </div>
  );
};
